<template>
  <div>
    <Loader :visible="isLoading" :text="loadingText" />

    <ConfirmationModal :show="showInfoModal" @close="showInfoModal = false">
      <template #title>
          <span :class="{
              'text-green-600': modalType === 'success',
              'text-red-600': modalType === 'error',
              'text-yellow-600': modalType === 'warning',
              'text-blue-600': modalType === 'info'
          }">
              <i class="fa mr-2" :class="{
                  'fa-check-circle': modalType === 'success',
                  'fa-exclamation-triangle': modalType === 'error' || modalType === 'warning',
                  'fa-info-circle': modalType === 'info'
              }"></i>
              {{ modalTitle }}
          </span>
      </template>

      <template #content>
          <div class="p-4">
              <p class="text-gray-700 text-lg whitespace-pre-line">{{ modalMessage }}</p>
          </div>
      </template>

      <template #footer>
          <PrimaryButton @click="showInfoModal = false">
              {{ $t('actions.ok') }}
          </PrimaryButton>
      </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showPrintPickingModal" @close="showPrintPickingModal = false">
      <template #title>
          {{ $t('gm.print_pickings') || 'Picking Generati' }}
      </template>

      <template #content>
        <div class="mt-4">
            <p class="mb-4 text-sm text-gray-600">Operazione completata. Puoi stampare i documenti generati:</p>
            <ul class="divide-y divide-gray-200 border rounded-md">
              <li v-for="(picking, index) in generatedPickings" :key="index"
                class="flex items-center justify-between p-4 bg-white border-b last:border-0 hover:bg-gray-100 transition-all duration-200">

                <div class="flex flex-col gap-0.5 text-left">
                    <span class="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Documento Generato</span>
                    <span class="text-sm font-medium text-gray-800">{{'Doc. No: '+ picking.DocumentNumber + ' Picking Id: ' + picking.DocumentId }}</span>
                </div>

                <div class="flex items-center gap-3">
                    <button
                        @click="printLabelPicking(picking)"
                        class="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-sm transition-all active:scale-95 group"
                        title="Stampa Etichetta"
                    >
                        <i class="fa fa-print"></i>
                        <span>Etichetta</span>
                    </button>

                    <button
                        @click="printPicking(picking)"
                        class="inline-flex items-center gap-2 px-3 py-2 bg-white hover:bg-gray-100 text-gray-700 border border-gray-300 text-xs font-semibold rounded-md shadow-sm transition-all active:scale-95 group"
                        title="Stampa Lista"
                    >
                        <i class="fa fa-print text-gray-400 group-hover:text-gray-600 transition-colors"></i>
                        <span>Picking List</span>
                    </button>
                </div>
              </li>
            </ul>
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="handleModalClose">
            {{ $t('core.close_and_continue') || 'Chiudi e prosegui' }}
        </SecondaryButton>
    </template>
    </ConfirmationModal>

    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6">

      <div v-if="suppliers.length === 0" class="flex flex-col items-center justify-center py-24 text-gray-400">
        <i class="fa fa-inbox text-5xl mb-4"></i>
        <p class="text-lg font-medium">Nessuna bolla in attesa di approvazione</p>
        <p class="text-sm mt-1">Le quantità registrate in modalità temporanea appariranno qui.</p>
      </div>

      <template v-else>

        <div class="space-y-4">
          <div
            v-for="(supplier, sIdx) in localSuppliers"
            :key="supplier.supplierId"
            class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          >
            <div
              class="w-full bg-gray-50 border-b border-gray-200 px-5 py-4 flex items-center justify-between gap-3"
            >
              <button
                type="button"
                class="flex items-center gap-3 text-left flex-1 min-w-0 hover:opacity-80 transition-opacity"
                @click="supplier.expanded = !supplier.expanded"
              >
                <i class="fa fa-building text-blue-500 flex-shrink-0"></i>
                <div class="min-w-0">
                  <span class="text-base font-semibold text-gray-800">
                    {{ supplier.supplierName || supplier.supplierId }}
                  </span>
                  <span class="ml-3 text-xs text-gray-500">
                    {{ totalRows(supplier) }} riga/righe &mdash;
                    {{ supplierSummary(supplier) }}
                  </span>
                </div>
              </button>

              <div class="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-600 transition-colors whitespace-nowrap"
                  @click.stop="selectSupplierRows(supplier, 'all')"
                >Seleziona tutto</button>
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded border border-green-300 bg-white hover:bg-green-50 text-green-700 transition-colors whitespace-nowrap"
                  @click.stop="selectSupplierRows(supplier, 'with_doc')"
                >Con DDT</button>
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded border border-orange-300 bg-white hover:bg-orange-50 text-orange-700 transition-colors whitespace-nowrap"
                  @click.stop="selectSupplierRows(supplier, 'without_doc')"
                >Senza DDT</button>
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-500 transition-colors whitespace-nowrap"
                  @click.stop="selectSupplierRows(supplier, 'none')"
                >Deseleziona</button>
                <i
                  :class="supplier.expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
                  class="text-gray-400 text-sm ml-1 cursor-pointer"
                  @click="supplier.expanded = !supplier.expanded"
                ></i>
              </div>
            </div>

            <div v-if="supplier.expanded">

              <div v-if="distinctDocsForSupplier(supplier).length > 0" class="border-b border-gray-200 bg-blue-50/30">
                <div class="px-5 py-2 bg-gray-50 border-b border-gray-100">
                  <h3 class="text-xs font-semibold text-gray-600 flex items-center gap-2">
                    <i class="fa fa-file-text text-green-500"></i>
                    Documenti Fornitore
                  </h3>
                </div>
                <div class="overflow-x-auto">
                  <table class="w-full text-sm border-collapse">
                    <thead>
                      <tr class="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide border-b border-gray-100">
                        <th class="text-left px-4 py-2 font-semibold">Doc. Fornitore</th>
                        <th class="text-left px-4 py-2 font-semibold">Righe</th>
                        <th class="text-left px-4 py-2 font-semibold">{{ $t('gm.supplier_doc_date') }}</th>
                        <th class="text-left px-4 py-2 font-semibold">{{ $t('gm.bill_of_lading_date') }}</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100">
                      <tr v-for="doc in distinctDocsForSupplier(supplier)" :key="doc" class="hover:bg-white/60">
                        <td class="px-4 py-2 font-mono font-medium text-gray-800">{{ doc }}</td>
                        <td class="px-4 py-2 text-center">
                          <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold">
                            {{ rowCountForDoc(supplier, doc) }}
                          </span>
                        </td>
                        <td class="px-4 py-2">
                          <VueDatePicker
                            :model-value="(docDates[docKey(supplier.supplierId, doc)] || {}).supplierDate"
                            @update:model-value="setDocDate(supplier.supplierId, doc, 'supplierDate', $event)"
                            model-type="yyyy-MM-dd"
                            :locale="$t('ui.datepicker_locale')"
                            :format="$t('ui.datepicker_format')"
                            :placeholder="$t('ui.choose_date')"
                            :enable-time-picker="false"
                            class="w-40 text-xs"
                            text-input
                            auto-apply
                            teleport
                            :max-date="(docDates[docKey(supplier.supplierId, doc)] || {}).bollaDate ? ((docDates[docKey(supplier.supplierId, doc)] || {}).bollaDate instanceof Date ? (docDates[docKey(supplier.supplierId, doc)] || {}).bollaDate.toLocaleDateString('en-CA') : String((docDates[docKey(supplier.supplierId, doc)] || {}).bollaDate).split('T')[0]) : undefined"
                          />
                        </td>
                        <td class="px-4 py-2">
                          <VueDatePicker
                            :model-value="(docDates[docKey(supplier.supplierId, doc)] || {}).bollaDate"
                            @update:model-value="setDocDate(supplier.supplierId, doc, 'bollaDate', $event)"
                            model-type="yyyy-MM-dd"
                            :locale="$t('ui.datepicker_locale')"
                            :format="$t('ui.datepicker_format')"
                            :placeholder="$t('ui.choose_date')"
                            :enable-time-picker="false"
                            class="w-40 text-xs"
                            text-input
                            auto-apply
                            teleport
                            :min-date="(docDates[docKey(supplier.supplierId, doc)] || {}).supplierDate ? ((docDates[docKey(supplier.supplierId, doc)] || {}).supplierDate instanceof Date ? (docDates[docKey(supplier.supplierId, doc)] || {}).supplierDate.toLocaleDateString('en-CA') : String((docDates[docKey(supplier.supplierId, doc)] || {}).supplierDate).split('T')[0]) : undefined"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="divide-y divide-gray-100">
              <div
                v-for="(group, gIdx) in supplier.subGroups"
                :key="group.groupKey"
                class="bg-white"
              >
                <button
                  type="button"
                  class="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-blue-50 transition-colors text-left"
                  @click="group.expanded = !group.expanded"
                >
                  <div class="flex items-center gap-3">
                    <i
                      :class="{
                        'fa fa-file-text text-green-500':    group.type === 'ddt',
                        'fa fa-shopping-cart text-blue-400': group.type === 'order',
                        'fa fa-plus-circle text-orange-400': group.type === 'excess',
                      }"
                      class="w-4 text-center"
                    ></i>
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="text-sm font-semibold text-gray-700">
                        <template v-if="group.type === 'ddt'">DDT {{ group.label }}</template>
                        <template v-else-if="group.type === 'order'">Ordine {{ group.label }}</template>
                        <template v-else>{{ group.label }}</template>
                      </span>
                      <span class="text-xs text-gray-400">{{ group.rows.length }} riga/righe</span>
                      <span
                        v-if="isGroupSomeSelected(group)"
                        class="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded"
                      >{{ selectedInGroup(group) }} selezionate</span>
                      <span
                        class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                        :class="{
                          'bg-green-50 text-green-700':   group.type === 'ddt',
                          'bg-blue-50 text-blue-700':     group.type === 'order',
                          'bg-orange-50 text-orange-700': group.type === 'excess',
                        }"
                      >
                        <i :class="{
                          'fa fa-truck':   group.type === 'ddt',
                          'fa fa-list-ol': group.type === 'order',
                          'fa fa-plus':    group.type === 'excess',
                        }" class="text-xs"></i>
                        <template v-if="group.type === 'ddt'">DDT</template>
                        <template v-else-if="group.type === 'order'">Ordine</template>
                        <template v-else>Eccedenza</template>
                      </span>
                    </div>
                  </div>
                  <i :class="group.expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" class="text-gray-400 text-xs flex-shrink-0"></i>
                </button>

                <div v-if="group.expanded" class="border-t border-gray-100">
                  <div class="overflow-x-auto">
                    <table class="w-full text-sm border-collapse">
                      <thead>
                        <tr class="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide border-b border-gray-200">
                          <th class="text-center px-4 py-2 font-semibold w-10">
                            <input
                              type="checkbox"
                              :checked="isGroupAllSelected(group)"
                              v-indeterminate="isGroupSomeSelected(group) && !isGroupAllSelected(group)"
                              :disabled="selectableRowsInGroup(group).length === 0"
                              @change="toggleGroupAll(group)"
                              class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            />
                          </th>
                          <th class="text-left px-4 py-2 font-semibold">{{ $t('core.order') }}</th>
                          <th class="text-left px-4 py-2 font-semibold">{{ $t('core.item') }}</th>
                          <th class="text-left px-4 py-2 font-semibold min-w-[160px]">{{ $t('core.description') }}</th>
                          <th class="text-left px-4 py-2 font-semibold">{{ $t('core.reference') }}</th>
                          <th class="text-right px-4 py-2 font-semibold">{{ $t('core.qty_order') }}</th>
                          <th class="text-right px-4 py-2 font-semibold">{{ $t('core.qty_registered') }}</th>
                          <th class="text-right px-4 py-2 font-semibold">{{ $t('core.registration') }}</th>
                          <th class="text-left px-4 py-2 font-semibold">{{ $t('core.uom') }}</th>
                          <th v-if="handleLots" class="text-left px-4 py-2 font-semibold">{{ $t('core.lot') }}</th>
                          <th v-if="handleLots && setValidityLotDate" class="text-left px-4 py-2 font-semibold">Scad.</th>
                          <th class="text-left px-4 py-2 font-semibold">{{ $t('core.location') }}</th>
                          <th class="text-left px-4 py-2 font-semibold">Doc. Fornitore</th>
                          <th class="text-left px-4 py-2 font-semibold">Registrazione</th>
                          <th class="text-center px-4 py-2 font-semibold">Stato</th>
                          <th class="text-center px-4 py-2 font-semibold w-10"></th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-100">
                        <tr
                          v-for="row in group.rows"
                          :key="row.PurchaseOrdId + '_' + row.Position"
                          class="hover:bg-blue-50 transition-colors"
                        >
                          <td class="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              :checked="!!selectedRows[rowKey(row)]"
                              :disabled="!isRowSelectable(row)"
                              @change="toggleRow(row)"
                              class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td class="px-4 py-2 font-mono text-xs text-gray-700">{{ row.InternalOrdNo }}</td>
                          <td class="px-4 py-2 font-mono font-medium text-gray-800">{{ row.Item }}</td>
                          <td class="px-4 py-2 text-gray-600 max-w-xs">{{ row.Description }}</td>
                          <td class="px-4 py-2 text-xs text-gray-700">
                            {{ [row.customer_id, row.SaleOrdNo, row.sale_ord_priority, row.Bin, row.jobDetails || row.Job].filter(x => x).join(' - ') }}
                          </td>
                          <td class="px-4 py-2 text-right tabular-nums text-gray-700">{{ row.Qty }}</td>
                          <td class="px-4 py-2 text-right tabular-nums text-gray-500">{{ row.DeliveredQty }}</td>
                          <td class="px-4 py-2">
                            <input
                              type="number"
                              v-model="row.CommitQty"
                              onfocus="this.select()"
                              min="0"
                              :max="Number(row.Qty) - Number(row.DeliveredQty || 0)"
                              class="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-right text-sm"
                            />
                          </td>
                          <td class="px-4 py-2 text-gray-600">{{ row.UoM }}</td>
                          <td v-if="handleLots" class="px-4 py-2 font-mono text-xs text-gray-700">
                            <template v-if="parseInt(row.UseLots) === 1">{{ row.Lot || '—' }}</template>
                            <span v-else class="text-gray-400 italic text-xs">Non gestito a lotti</span>
                          </td>
                          <td v-if="handleLots && setValidityLotDate" class="px-4 py-2 text-xs text-gray-500">
                            <template v-if="parseInt(row.UseLots) === 1">{{ row.ValidTo ? formatDate(row.ValidTo) : '—' }}</template>
                            <span v-else class="text-gray-400">—</span>
                          </td>
                          <td class="px-4 py-2 text-xs text-gray-600">{{ row.Bin || '—' }}</td>
                          <td class="px-4 py-2">
                            <input
                              v-model="row.supplierDoc"
                              type="text"
                              placeholder="Num. DDT / Bolla"
                              class="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-32"
                              @change="ensureDocDate(supplier.supplierId, row.supplierDoc)"
                            />
                          </td>
                          <td class="px-4 py-2 text-xs text-gray-500">
                            <div class="font-medium text-gray-700">{{ userMap[row.UserId] || row.UserId || '—' }}</div>
                            <div class="text-[10px] text-gray-400 whitespace-nowrap">{{ formatDateTime(row.UpdatedAt) }}</div>
                          </td>
                          <td class="px-4 py-2 text-center">
                            <div class="flex justify-center items-center">
                              <!-- 3) Anomalia: Ordine Già Evaso -->
                              <i v-if="row.SaleOrdIsPicked"
                                class="fa fa-times-circle text-red-500 text-xl"
                                title="ERRORE: Ordine cliente già evaso/chiuso"></i>

                              <!-- 1) Picking parziale presente -->
                              <i v-else-if="row.SaleOrdPickedQty > 0"
                                class="fa fa-truck text-blue-500 text-xl"
                                :title="'Picking parziale esistente: ' + row.SaleOrdPickedQty"></i>

                              <!-- 2) Qta in eccesso -->
                              <i v-else-if="Number(row.CommitQty) > Number(row.SaleOrdRemaining)"
                                class="fa fa-exclamation-circle text-orange-500 text-xl"
                                title="Attenzione: Quantità superiore al residuo ordine cliente"></i>

                              <i v-else-if="Number(row.CommitQty) > (Number(row.Qty) - Number(row.DeliveredQty))"
                                class="fa fa-exclamation-triangle text-yellow-500 text-xl"
                                title="Attenzione: Quantità superiore al residuo ordine fornitore"></i>

                              <i v-else-if="Number(row.CommitQty) <= 0"
                                class="fa fa-minus-circle text-gray-400 text-xl"
                                title="Non ancora registrato"></i>

                              <!-- 4) Tutto OK -->
                              <i v-else class="fa fa-check-circle text-green-500 text-xl" title="OK"></i>
                            </div>
                          </td>
                          <td class="px-4 py-2 text-center">
                            <button
                              type="button"
                              class="text-red-500 hover:text-red-700 transition-colors p-1"
                              @click.stop="deleteRow(row)"
                              title="Elimina riga"
                            >
                              <i class="fa fa-trash text-lg"></i>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>


                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

      </template>

      <div class="pb-24"></div>
    </div>

    <div v-if="selectedCount > 0" class="fixed left-0 right-0 bottom-0 z-50 pointer-events-none">
      <div class="pointer-events-auto bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex items-center justify-center gap-4">
        <span class="text-sm text-gray-600">
          <span class="font-semibold text-blue-700">{{ selectedCount }}</span> riga/righe selezionate
        </span>

        <template v-if="selectionState === 'all_doc'">
          <button
            type="button"
            class="inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            @click="approvaBolle"
          >
            <i class="fa fa-paper-plane"></i>
            Invia a Mago
          </button>
        </template>

        <template v-else-if="selectionState === 'mixed'">
          <span class="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-yellow-800 bg-yellow-50 border border-yellow-200">
            <i class="fa fa-exclamation-triangle"></i>
            Selezione mista: alcune righe hanno DDT e altre no. Seleziona solo righe dello stesso tipo.
          </span>
        </template>

        <template v-else-if="selectionState === 'mixed_supplier'">
          <span class="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-yellow-800 bg-yellow-50 border border-yellow-200">
            <i class="fa fa-exclamation-triangle"></i>
            Fornitori diversi: le righe senza DDT selezionate appartengono a fornitori diversi. Seleziona righe dello stesso fornitore.
          </span>
        </template>

        <template v-else-if="selectionState === 'all_no_doc'">
          <input
            v-model="massDDT"
            type="text"
            placeholder="Numero DDT / Bolla"
            class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 text-sm w-48"
            @keyup.enter="applyMassDDT"
          />
          <button
            type="button"
            :disabled="!massDDT.trim()"
            class="inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            @click="applyMassDDT"
          >
            <i class="fa fa-tag"></i>
            Assegna DDT
          </button>
        </template>
      </div>
    </div>

    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import { router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import Loader from '@/Components/Loader.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import axios from 'axios';
</script>

<script>
const getTodayDateString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default {
  props: {
    suppliers:          { type: Array,   default: () => [] },
    userMap:            { type: Object,  default: () => ({}) },
    handleLots:         { type: Boolean, default: false },
    setValidityLotDate: { type: Boolean, default: false },
  },

  directives: {
    indeterminate: {
      mounted(el, binding)  { el.indeterminate = !!binding.value; },
      updated(el, binding)  { el.indeterminate = !!binding.value; },
    },
  },

  data() {
    const initialDocDates = {};
    for (const s of this.suppliers) {
      for (const g of s.subGroups || []) {
        if (g.type === 'ddt' && g.suggestedSupplierDoc) {
          const key = s.supplierId + '::' + g.suggestedSupplierDoc;
          initialDocDates[key] = { supplierDate: getTodayDateString(), bollaDate: getTodayDateString() };
        }
      }
    }

    return {
      navigation: [
        { name: 'Dashboard',                          href: route('dashboard') },
        { name: this.$t('modules.gestione-magazzino'), href: route('modules.home', 'gestione-magazzino') },
        { name: 'Approvazione Bolle Temporanee',       href: route('modules.gm.approvazioneBolleTemp') },
      ],
      localSuppliers: this.suppliers.map(s => ({
        ...s,
        expanded: true,
        subGroups: (s.subGroups || []).map(g => ({
          ...g,
          expanded: false,
          rows: (g.rows || []).map(r => ({
            ...r,
            supplierDoc: g.type === 'ddt' ? (g.suggestedSupplierDoc || '') : '',
          })),
        })),
      })),
      docDates: initialDocDates,
      selectedRows: {},
      isLoading:     false,
      loadingText:   '',
      showInfoModal: false,
      modalTitle:    '',
      modalMessage:  '',
      modalType:     'info',
      toast: { show: false, message: '', type: 'success' },
      massDDT: '',
      showPrintPickingModal: false,
      generatedPickings: [],
      lastBollaId: null,
      pendingRedirect: false,
    };
  },

  computed: {
    selectedCount() {
      return Object.values(this.selectedRows).filter(Boolean).length;
    },

    selectedRowsList() {
      const list = [];
      for (const s of this.localSuppliers) {
        for (const g of s.subGroups || []) {
          for (const r of g.rows || []) {
            if (this.selectedRows[this.rowKey(r)]) {
              list.push({ row: r, supplier: s });
            }
          }
        }
      }
      return list;
    },

    selectionState() {
      if (this.selectedCount === 0) return 'none';
      const hasDoc   = this.selectedRowsList.some(({ row }) => (row.supplierDoc || '').trim() !== '');
      const hasNoDoc = this.selectedRowsList.some(({ row }) => (row.supplierDoc || '').trim() === '');
      if (hasDoc && hasNoDoc) return 'mixed';
      if (hasDoc) return 'all_doc';
      const suppliers = new Set(this.selectedRowsList.map(({ supplier }) => supplier.supplierId));
      if (suppliers.size > 1) return 'mixed_supplier';
      return 'all_no_doc';
    },
  },

  methods: {
    docKey(supplierId, doc) {
      return supplierId + '::' + doc;
    },

    distinctDocsForSupplier(supplier) {
      const docs = new Set();
      for (const g of supplier.subGroups || []) {
        for (const r of g.rows || []) {
          const doc = (r.supplierDoc || '').trim();
          if (doc) docs.add(doc);
        }
      }
      return [...docs];
    },

    rowCountForDoc(supplier, doc) {
      let count = 0;
      for (const g of supplier.subGroups || []) {
        for (const r of g.rows || []) {
          if ((r.supplierDoc || '').trim() === doc) count++;
        }
      }
      return count;
    },

    ensureDocDate(supplierId, doc) {
      const d = (doc || '').trim();
      if (!d) return;
      const key = supplierId + '::' + d;
      if (!this.docDates[key]) {
        this.docDates[key] = { supplierDate: getTodayDateString(), bollaDate: getTodayDateString() };
      }
    },

    setDocDate(supplierId, doc, field, value) {
      const key = supplierId + '::' + doc;
      if (!this.docDates[key]) {
        this.docDates[key] = { supplierDate: getTodayDateString(), bollaDate: getTodayDateString() };
      }
      this.docDates[key][field] = value;
    },

    selectableRowsInGroup(group) {
      return (group.rows || []).filter(r => this.isRowSelectable(r));
    },

    isGroupAllSelected(group) {
      const selectable = this.selectableRowsInGroup(group);
      return selectable.length > 0 && selectable.every(r => !!this.selectedRows[this.rowKey(r)]);
    },

    isGroupSomeSelected(group) {
      const selectable = this.selectableRowsInGroup(group);
      return selectable.some(r => !!this.selectedRows[this.rowKey(r)]);
    },

    selectedInGroup(group) {
      return (group.rows || []).filter(r => !!this.selectedRows[this.rowKey(r)]).length;
    },

    toggleGroupAll(group) {
      const selectable = this.selectableRowsInGroup(group);
      const allSelected = this.isGroupAllSelected(group);
      for (const r of selectable) {
        this.selectedRows[this.rowKey(r)] = !allSelected;
      }
    },

    rowKey(row) {
      return row.PurchaseOrdId + '::' + row.Position;
    },

    isRowSelectable(row) {
      // Non permettiamo di selezionare righe con errori bloccanti (es. ordine cliente già evaso)
      if (row.SaleOrdIsPicked) return false;
      return true;
    },

    selectSupplierRows(supplier, mode) {
      for (const g of supplier.subGroups || []) {
        for (const r of g.rows || []) {
          const key = this.rowKey(r);
          const hasDoc = (r.supplierDoc || '').trim() !== '';
          if (mode === 'all') {
            this.selectedRows[key] = true;
          } else if (mode === 'none') {
            this.selectedRows[key] = false;
          } else if (mode === 'with_doc') {
            this.selectedRows[key] = hasDoc;
          } else if (mode === 'without_doc') {
            this.selectedRows[key] = !hasDoc;
          }
        }
      }
    },

    applyMassDDT() {
      const doc = this.massDDT.trim();
      if (!doc) return;
      for (const { row, supplier } of this.selectedRowsList) {
        if ((row.supplierDoc || '').trim() === '') {
          row.supplierDoc = doc;
          this.ensureDocDate(supplier.supplierId, doc);
        }
      }
      this.massDDT = '';
    },

    toggleRow(row) {
      const key = this.rowKey(row);
      this.selectedRows[key] = !this.selectedRows[key];
    },

    buildSubmissions() {
      const groups = {};
      for (const s of this.localSuppliers) {
        for (const g of s.subGroups || []) {
          for (const r of g.rows || []) {
            const key = this.rowKey(r);
            if (!this.selectedRows[key]) continue;

            const doc = (r.supplierDoc || '').trim();
            if (!doc) continue;

            const groupKey = s.supplierId + '::' + doc;
            if (!groups[groupKey]) {
              const dates = this.docDates[this.docKey(s.supplierId, doc)] || {};
              const formatLocalDate = (d) => {
                if (!d) return null;
                if (d instanceof Date) {
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  return `${y}-${m}-${day}`;
                }
                return String(d).split('T')[0];
              };
              groups[groupKey] = {
                supplierId: s.supplierId,
                supplierDoc: doc,
                supplierDate: formatLocalDate(dates.supplierDate),
                billofLadingDate: formatLocalDate(dates.bollaDate),
                rows: [],
              };
            }
            
            // Send only needed fields to avoid Proxy issues and ensure correct types
            groups[groupKey].rows.push({
              id_riga_portale: r.id_riga_portale,
              PurchaseOrdId: r.PurchaseOrdId,
              Position:      r.Position,
              Item:          r.Item,
              Description:   r.Description,
              CommitQty:     Number(r.CommitQty || 0),
              Lot:           r.Lot,
              ValidTo:       r.ValidTo,
              Matricola:     r.Matricola,
              Bin:           r.Bin,
              UoM:           r.UoM,
              InternalOrdNo: r.InternalOrdNo,
              Supplier:      r.Supplier,
              Qty:           r.Qty,
              DeliveredQty:  r.DeliveredQty,
              SaleOrdId:     r.SaleOrdId,
              SaleOrdPos:    r.SaleOrdPos,
              SaleOrdNo:     r.SaleOrdNo
            });
          }
        }
      }
      return Object.values(groups);
    },

    async approvaBolle() {
      const submissions = this.buildSubmissions();
      if (!submissions.length) return;

      // Validazione Date e Lotti
      const getDateOnly = (date) => {
        if (!date) return null;
        if (date instanceof Date) {
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        }
        return String(date).split('T')[0];
      };

      for (const sub of submissions) {
        const sDate = getDateOnly(sub.supplierDate);
        const bDate = getDateOnly(sub.billofLadingDate);

        if (sDate && bDate && sDate > bDate) {
          this.showToast(
            (this.$t('gm.supplier_date_must_be_before_billing_date') || 'Data documento fornitore deve essere minore o uguale a data registrazione bolla di carico') +
            ` (DDT: ${sub.supplierDoc})`,
            'error'
          );
          return;
        }

        if (this.handleLots) {
          if (sub.rows.some(r => parseInt(r.UseLots) === 1 && (!r.Lot || r.Lot.trim() === ''))) {
            this.showToast(this.$t('core.lot_required_warning') + ` (DDT: ${sub.supplierDoc})`, 'warning');
            return;
          }
        }
      }

      this.isLoading   = true;
      this.loadingText = 'Invio a Mago in corso...';

      try {
        const response = await axios.post(route('modules.gm.approvaBolle'), { submissions });
        const data = response.data;

        const successKeys = new Set(
          (data.results || [])
            .filter(r => r.success)
            .map(r => r.supplierId + '::' + r.supplierDoc)
        );

        if (successKeys.size > 0) {
          for (const s of this.localSuppliers) {
            for (const g of s.subGroups) {
              g.rows = g.rows.filter(r => {
                const gk = s.supplierId + '::' + (r.supplierDoc || '').trim();
                return !this.selectedRows[this.rowKey(r)] || !successKeys.has(gk);
              });
            }
            s.subGroups = s.subGroups.filter(g => g.rows.length > 0);
          }
          this.localSuppliers = this.localSuppliers.filter(s => s.subGroups.length > 0);
          this.selectedRows = {};
        }

        const allResults = data.results || [];
        const pickingEnabled = allResults.some(r => r.details?.picking?.enabled);
        const pickingResults = allResults.flatMap(r => r.details?.picking?.results || []);

        if (data.success) {
          const allBollaData = allResults.map(r => r.details?.bolla?.data).filter(Boolean);
          const ddtMapping = [];
          
          allBollaData.forEach(bollaData => {
            Object.entries(bollaData).forEach(([ddt, b]) => {
              const docNo = b?.Data?.PurchaseDocument?.DocNo;
              if (docNo) {
                ddtMapping.push(`• DDT: ${ddt} → Protocollo: ${docNo}`);
              }
            });
          });
          
          const DocNo = ddtMapping.length > 0 ? ddtMapping.join('\n') : '';

          if (pickingEnabled && pickingResults.length > 0) {
            this.showToast('Bolle create con successo - ' + this.$t('gm.print_pickings'), 'success', DocNo);
            this.generatedPickings = pickingResults;
            this.showPrintPickingModal = true;
          } else {
            let finalMessage = 'Bolle create con successo';
            if (pickingEnabled && pickingResults.length === 0) {
              finalMessage += ' ' + this.$t('gm.ddt_flow.picking_no_orders');
            }
            this.showToast(finalMessage, 'success', DocNo);
            this.executeRedirect(null, 3000);
          }
        } else {
          const failedResults = allResults.filter(r => !r.success);
          const failedDocs = failedResults.map(r => r.supplierDoc).join(', ');
          let errorMsg = 'Alcuni documenti non sono stati creati: ' + failedDocs;

          const pickingSuccess = allResults.some(r => r.details?.picking?.success);

          if (pickingEnabled && !pickingSuccess) {
            const pickingMessage = allResults.find(r => r.details?.picking?.message)?.details?.picking?.message || 'Errore picking';
            errorMsg += '\n\n⚠️ Bolle create, ma picking non generato:\n' + pickingMessage;
          }

          this.showToast(errorMsg, 'error');
        }
      } catch (e) {
        this.showToast('Errore durante l\'invio a Mago', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async deleteRow(row) {
      if (!confirm('Sei sicuro di voler eliminare questa riga?')) return;

      try {
        const response = await axios.post(route('modules.gm.deleteTempRow'), {
          purchase_ord_id: row.PurchaseOrdId,
          position:        row.Position
        });

        if (response.data.success) {
          this.showToast('Riga eliminata con successo', 'success');

          // Rimuovi localmente
          for (const s of this.localSuppliers) {
            for (const g of s.subGroups) {
              const idx = g.rows.findIndex(r => r.PurchaseOrdId === row.PurchaseOrdId && r.Position === row.Position);
              if (idx !== -1) {
                g.rows.splice(idx, 1);
                break;
              }
            }
            s.subGroups = s.subGroups.filter(g => g.rows.length > 0);
          }
          this.localSuppliers = this.localSuppliers.filter(s => s.subGroups.length > 0);
          delete this.selectedRows[this.rowKey(row)];
        } else {
          this.showToast('Errore durante l\'eliminazione: ' + (response.data.message || 'Errore generico'), 'error');
        }
      } catch (e) {
        this.showToast('Errore durante l\'eliminazione', 'error');
      }
    },

    totalRows(supplier) {
      return (supplier.subGroups || []).reduce((sum, g) => sum + (g.rows || []).length, 0);
    },

    supplierSummary(supplier) {
      const groups = supplier.subGroups || [];
      const ddts   = groups.filter(g => g.type === 'ddt').length;
      const orders = groups.filter(g => g.type === 'order').length;
      const excess = groups.filter(g => g.type === 'excess').length;
      const parts  = [];
      if (ddts)   parts.push(ddts   + (ddts   === 1 ? ' DDT'    : ' DDT'));
      if (orders) parts.push(orders + (orders === 1 ? ' ordine' : ' ordini'));
      if (excess) parts.push('righe in eccesso');
      return parts.join(', ') || '—';
    },

    showToast(message, type = 'success', extraMessage = '') {
      this.toast.message = message;
      this.toast.type    = type;
      this.toast.show    = true;

      this.modalMessage = message + (extraMessage ? '\n\n' + extraMessage : '');
      this.modalType = type;
      switch(type) {
          case 'success': this.modalTitle = this.$t('core.successful_operation'); break;
          case 'error': this.modalTitle = this.$t('core.error'); break;
          case 'warning': this.modalTitle = this.$t('core.warning'); break;
          default: this.modalTitle = this.$t('core.info');
      }
      this.showInfoModal = true;
    },

    printPicking(picking) {
      const id = picking.DocumentId;
      if (!id) {
        this.showToast("Impossibile trovare l'ID del picking", "error");
        return;
      }
      const url = route('modules.gm.print-picking-list', { DocId: id });
      const win = window.open(url, '_blank');
      if (win) {
          win.focus();
      } else {
          this.showToast("Il browser ha bloccato il popup di stampa", "warning");
      }
    },

    printLabelPicking(picking) {
      const id = picking.DocumentId;
      if (!id) {
        this.showToast("Impossibile trovare l'ID del picking", "error");
        return;
      }
      const url = route('modules.gm.print-picking-label', { DocId: id });
      const win = window.open(url, '_blank');
      if (win) {
          win.focus();
      } else {
          this.showToast("Il browser ha bloccato il popup di stampa", "warning");
      }
    },

    executeRedirect(bollaId, duration = 1000) {
      setTimeout(() => {
        router.reload();
      }, duration);
    },

    handleModalClose() {
        this.showPrintPickingModal = false;
        this.executeRedirect(this.lastBollaId);
    },
  },
};
</script>
